import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Upload, ArrowLeft, Loader2, FileText, MapPin, Phone, Mail, Clock, Save, CheckCircle } from "lucide-react";

const BUSINESS_TYPES = [
  { value: "private_sports_facility", label: "Private Sports Facility" },
  { value: "gym_studio", label: "Gym / Studio" },
  { value: "lgu_community", label: "LGU / Community" },
  { value: "mixed_use", label: "Mixed-Use / Multi-Purpose" },
];

const SPORT_OPTIONS = [
  "Basketball", "Badminton", "Volleyball", "Tennis", "Table Tennis",
  "Football", "Swimming", "Boxing", "Martial Arts", "Yoga",
  "Dance", "CrossFit", "Gym/Weights", "Running Track", "Other",
];

export default function NewVenue() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: "", address: "", city: "", description: "",
    business_type: "", sport_types: [],
    business_registration_number: "", legal_entity_name: "", authorized_signatory_name: "",
    government_authorization: "", lgu_contact_person: "", lgu_contact_number: "",
    contact_phone: "", contact_email: "", operating_hours: "",
  });
  const [docs, setDocs] = useState([]);

  const isLGU = form.business_type === "lgu_community";
  const isPrivate = ["private_sports_facility", "gym_studio", "mixed_use"].includes(form.business_type);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const toggleSport = (sport) => {
    setForm(prev => ({
      ...prev,
      sport_types: prev.sport_types.includes(sport)
        ? prev.sport_types.filter(s => s !== sport)
        : [...prev.sport_types, sport],
    }));
  };

  const handleDocUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.file_url || res.data.url;
      setDocs(prev => [...prev.filter(d => d.doc_type !== docType), { doc_type: docType, file_url: url, file_name: file.name, uploaded_at: new Date().toISOString() }]);
      toast.success(`${docType} uploaded`);
    } catch {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.address || !form.city || !form.business_type) {
      toast.error("Please fill in all required fields (Name, Address, City, Business Type)");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, verification_documents: docs };
      const res = await api.post("/venues", payload);
      toast.success("Venue submitted for verification! Check your email.");
      navigate("/provider/venues");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create venue");
    }
    setSaving(false);
  };

  const requiredDocs = isLGU
    ? [
        { key: "gov_authorization", label: "Government / Community Authorization Letter" },
        { key: "facility_photos", label: "Facility Photos" },
        { key: "contact_proof", label: "LGU Contact Proof / ID" },
      ]
    : [
        { key: "business_permit", label: "Business Permit / Registration" },
        { key: "facility_photos", label: "Facility Photos" },
        { key: "proof_of_ownership", label: "Proof of Ownership / Lease Agreement" },
        ...(form.business_type === "mixed_use" ? [{ key: "multi_use_authorization", label: "Multi-Use Authorization" }] : []),
      ];

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 pb-20" data-testid="new-venue-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />New Venue
          </h1>
          <p className="text-sm text-muted-foreground">Set up your venue details and submit for verification</p>
        </div>
      </div>

      {/* ── Venue Details ── */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4" />Venue Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Venue Name *</Label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Manila Sports Center" data-testid="venue-name" />
          </div>
          <div>
            <Label>Address *</Label>
            <Input value={form.address} onChange={e => set("address", e.target.value)} placeholder="Full street address" data-testid="venue-address" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>City *</Label>
              <Input value={form.city} onChange={e => set("city", e.target.value)} placeholder="City / Municipality" data-testid="venue-city" />
            </div>
            <div>
              <Label>Operating Hours</Label>
              <Input value={form.operating_hours} onChange={e => set("operating_hours", e.target.value)} placeholder="e.g. Mon-Sat 6AM-10PM" data-testid="venue-hours" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Describe your venue, facilities, and what makes it special..." rows={3} data-testid="venue-description" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Contact Phone</Label><Input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} placeholder="09XX XXX XXXX" data-testid="venue-phone" /></div>
            <div><Label>Contact Email</Label><Input value={form.contact_email} onChange={e => set("contact_email", e.target.value)} placeholder="venue@email.com" data-testid="venue-email" /></div>
          </div>
          <div>
            <Label className="mb-2 block">Sports & Activities Available</Label>
            <div className="flex flex-wrap gap-2">
              {SPORT_OPTIONS.map(sport => (
                <button key={sport} type="button" onClick={() => toggleSport(sport)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.sport_types.includes(sport) ? "bg-court-green text-white border-court-green" : "bg-white text-slate-600 border-slate-200 hover:border-court-green/50"}`}
                  data-testid={`sport-${sport.toLowerCase().replace(/\s/g, "-")}`}>
                  {sport}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Business Information ── */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" />Business Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Business Type *</Label>
            <Select value={form.business_type} onValueChange={v => set("business_type", v)}>
              <SelectTrigger data-testid="business-type-select"><SelectValue placeholder="Select business type" /></SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPES.map(bt => <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Private / Gym / Mixed fields */}
          {isPrivate && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border" data-testid="private-business-fields">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Private Business Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Business Registration #</Label><Input value={form.business_registration_number} onChange={e => set("business_registration_number", e.target.value)} placeholder="SEC / DTI / CDA #" data-testid="biz-reg-number" /></div>
                <div><Label>Legal Entity Name</Label><Input value={form.legal_entity_name} onChange={e => set("legal_entity_name", e.target.value)} placeholder="Registered business name" data-testid="legal-entity" /></div>
              </div>
              <div><Label>Authorized Signatory</Label><Input value={form.authorized_signatory_name} onChange={e => set("authorized_signatory_name", e.target.value)} placeholder="Full name of authorized representative" data-testid="signatory-name" /></div>
            </div>
          )}

          {/* LGU / Community fields */}
          {isLGU && (
            <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200" data-testid="lgu-community-fields">
              <p className="text-xs font-medium text-green-700 uppercase tracking-wider">Government / Community Details</p>
              <div><Label>Government / Community Authorization</Label><Textarea value={form.government_authorization} onChange={e => set("government_authorization", e.target.value)} placeholder="Describe the authorization (e.g., City Council resolution, Barangay authorization letter)" rows={2} data-testid="gov-authorization" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>LGU Contact Person</Label><Input value={form.lgu_contact_person} onChange={e => set("lgu_contact_person", e.target.value)} placeholder="Name of point person" data-testid="lgu-contact" /></div>
                <div><Label>LGU Contact Number</Label><Input value={form.lgu_contact_number} onChange={e => set("lgu_contact_number", e.target.value)} placeholder="Office / mobile number" data-testid="lgu-phone" /></div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Required Documents ── */}
      {form.business_type && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4" />Required Documents</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">Upload the following documents for verification. Accepted formats: PDF, JPG, PNG.</p>
            {requiredDocs.map(doc => {
              const uploaded = docs.find(d => d.doc_type === doc.key);
              return (
                <div key={doc.key} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`doc-upload-${doc.key}`}>
                  <div className="flex items-center gap-3 flex-1">
                    <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{doc.label}</p>
                      {uploaded && <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />{uploaded.file_name}</p>}
                    </div>
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => handleDocUpload(e, doc.key)} disabled={uploading} />
                    <span className={`px-3 py-1.5 text-xs font-medium rounded-full border ${uploaded ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                      {uploading ? "Uploading..." : uploaded ? "Replace" : "Upload"}
                    </span>
                  </label>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* ── Submit ── */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={() => navigate(-1)} className="flex-1" data-testid="cancel-btn">Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving || uploading} className="flex-1 bg-court-green hover:bg-court-green-hover text-white" data-testid="submit-venue-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : <><Save className="w-4 h-4 mr-2" />Submit for Verification</>}
        </Button>
      </div>
    </div>
  );
}
